import React, { useEffect, useMemo, useState } from 'react';
import { createEmployee, updateEmployee } from '../services/api.ts';
import type { Employee } from '../services/api.ts';
import { useCredentials, useHospitals } from '../data/queries.ts';
import Modal from './Modal.tsx';
import ProviderForm, { EMPTY_PROVIDER_FORM, ProviderFormValues, providerFormFromEmployee } from './ProviderForm.tsx';

// Create or edit one provider in a dialog, so the Providers page leads with its table and the long form
// only appears when someone asks for it. Saving goes through services/api.ts, which marks the cached
// employee list stale — the Providers table and the Credentials table both refresh on their own.

interface ProviderFormDialogProps {
  open: boolean;
  /** The provider being edited, or null to create a new one. */
  employee: any | null;
  onClose: () => void;
  /** Called after a successful save with a confirmation message for the page to show. */
  onSaved: (message: string) => void;
}

const FORM_ID = 'provider-form';

const ProviderFormDialog: React.FC<ProviderFormDialogProps> = ({ open, employee, onClose, onSaved }) => {
  const editing = !!employee;
  const initial = useMemo<ProviderFormValues>(
    () => (employee ? providerFormFromEmployee(employee) : EMPTY_PROVIDER_FORM),
    [employee]
  );
  const [formData, setFormData] = useState<ProviderFormValues>(initial);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hospitalsQuery = useHospitals();
  const credentialsQuery = useCredentials();

  // Each time the dialog opens (or switches to a different provider) it starts from that record.
  useEffect(() => {
    if (open) {
      setFormData(initial);
      setErrorMessage(null);
    }
  }, [open, initial]);

  const dirty = JSON.stringify(formData) !== JSON.stringify(initial);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    // Sent as-is, as the old Employee Cards form did: an empty studentCompetency ('') is how "Not set" is
    // stored (and how an update clears it), which the narrower Employee type doesn't spell out.
    const payload = formData as unknown as Omit<Employee, 'employeeId'>;
    try {
      if (editing) {
        await updateEmployee(employee.employeeId, payload);
        onSaved(`${formData.firstName} ${formData.lastName} updated.`);
      } else {
        await createEmployee(payload);
        onSaved(`${formData.firstName} ${formData.lastName} added.`);
      }
      onClose();
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.error || `Failed to ${editing ? 'update' : 'create'} provider`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!saving) onClose(); }}
      title={editing ? 'Edit provider' : 'Create provider'}
      closeOnBackdrop={!dirty && !saving}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={FORM_ID} className="btn btn-primary" disabled={saving}>
            {saving ? (editing ? 'Saving…' : 'Creating…') : (editing ? 'Save changes' : 'Create provider')}
          </button>
        </>
      }
    >
      {errorMessage && (
        <div className="alert alert-error" role="alert">{errorMessage}</div>
      )}
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <ProviderForm
          value={formData}
          onChange={setFormData}
          hospitals={hospitalsQuery.data ?? []}
          credentialOptions={credentialsQuery.data ?? []}
          credentialsError={credentialsQuery.isError ? 'Failed to load credentials' : null}
        />
      </form>
    </Modal>
  );
};

export default ProviderFormDialog;
